/**
 * Keeps React alive when the browser translates the page.
 *
 * Browser translators (Chrome's "Translate this page", Edge, extensions)
 * replace text nodes under React's feet, wrapping them in <font> elements.
 * The next time React updates or removes one of those nodes it calls
 * removeChild / insertBefore on a parent that no longer holds it, the DOM
 * throws NotFoundError, and the whole app falls into the top-level
 * ErrorBoundary ("Something went wrong / Error in: App"). Production,
 * 2026-10-16: a survey builder user saw exactly that on save, with no request
 * ever reaching the backend.
 *
 * The guard turns those two impossible operations into no-ops, the
 * workaround discussed in facebook/react#11538, so the screen keeps working
 * while translated. Every time it acts it reports once per page load to
 * Sentry, tagged with whether the page is translated, so we can measure it.
 * Backend docs: backend/docs/FRONTEND_BROWSER_TRANSLATION_GUARD.md
 */

const INSTALLED = Symbol.for('grid.translationGuard.installed');

export const isPageTranslated = () => {
  if (typeof document === 'undefined') return false;
  const root = document.documentElement;
  const className = String(root?.className || '');
  return /\btranslated-(ltr|rtl)\b/.test(className) || Boolean(document.querySelector('font[style*="vertical-align"]'));
};

let reported = false;

const defaultReporter = (operation) => {
  if (reported) return;
  reported = true;
  const translated = isPageTranslated();
  import('./sentry.js')
    .then(({ captureMessage }) =>
      captureMessage('DOM mutation outside React prevented a crash', 'warning', {
        tags: { translation_guard: operation, page_translated: String(translated) },
        extra: { lang: document.documentElement?.lang || '', path: window.location?.pathname || '' },
      }),
    )
    .catch(() => {});
};

export const installTranslationGuard = (
  reporter = defaultReporter,
  proto = typeof Node === 'function' ? Node.prototype : null,
) => {
  if (!proto || proto[INSTALLED]) return false;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function removeChild(child) {
    if (child && child.parentNode !== this) {
      reporter('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      reporter('insertBefore');
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };

  Object.defineProperty(proto, INSTALLED, { value: true });
  return true;
};

installTranslationGuard();
